import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { Button, Theme, TextField } from '@material-ui/core';
import IdleTimer from 'react-idle-timer';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import { useState, useEffect } from 'react';
import { RootState } from '../../store/rootReducer';
import { logOut, login, setSessionExpired } from '../../store/modules/authSlice';
import { useHistory } from "react-router-dom";
import Alert from '@material-ui/lab/Alert';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { useForm, Controller } from "react-hook-form";
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { amountActiveSessionExceeded } from '../../common/phrases';
import IUserData from '../../interfaces/IUserData';
import eventNames from '../../store/signalr/eventNames';
import { addTaskForEvent, removeTaskForEvent } from '../../store/signalr/tasksForEvents';

const useStyles = makeStyles((theme: Theme) => ({

    alertErrorRoot: {
        marginTop: 24
    }
}));

let logOutIdleTimeout: NodeJS.Timeout | null;
let idleTimeInMinutes = 60;
let logOutTimeOutInMinutes = 5;

const setIdleTimes = (userData: IUserData | null) => {
    if(!userData){
        return;
    }
    // As per the SMA's suggession on ticket 67, the below codeblock is commented to enable the 
    // application to have same Idle time and Logout time for all the user profiles

    // if(userData.idleTimeInMinutes && userData.idleTimeInMinutes > 0){
    //     idleTimeInMinutes = userData.idleTimeInMinutes;
    // }
    // if(userData.logOutTimeOutInMinutes && userData.logOutTimeOutInMinutes > 0){
    //     logOutTimeOutInMinutes = userData.logOutTimeOutInMinutes;
    // }
}

const componentTaskId = "autoLogout";

function AutoLogout(): JSX.Element {


    const { handleSubmit, errors, control, formState } = useForm();

    const classes = useStyles();
    const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();

    const history = useHistory();
    const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

    const { authStore } = useSelector((state: RootState) => state);
    const [errorMessage, setErrorMessage] = useState("");

    const handleOnIdle = () => {
        if(isDialogOpen)
            return;
        setDialogOpen(true);
        dispatch(setSessionExpired());
        logOutIdleTimeout = setTimeout(handleLogOut, logOutTimeOutInMinutes * 60 * 1000);
    }

    
    
    const whenLogoutUserActiveSessionsDo = (exceededUserActiveSessionIds: string[]) =>{
        const foundId = exceededUserActiveSessionIds.find(q=>q === authStore?.data?.userActiveSessionId);
        if(foundId){
            handleOnIdle();
        }
    }

    useEffect(() => {
        // if page reshreshed with expired session => logout
        if (authStore.sessionExpired) {
            dispatch(logOut());
        }
    }, []);

    useEffect(() => {
        addTaskForEvent(eventNames.logoutUserActiveSessions, componentTaskId, whenLogoutUserActiveSessionsDo);
        return function () {
            removeTaskForEvent(eventNames.logoutUserActiveSessions, componentTaskId);
        }
    }, [whenLogoutUserActiveSessionsDo]);

    let idleTimer: IdleTimer | null;

    const handleOnReturnToLogin = () => {
        if (logOutIdleTimeout !== null) {
            clearTimeout(logOutIdleTimeout);
        }
        dispatch(logOut());
        history.push('/login');
    }
    const handleOnLogin = async (data: any): Promise<void> => {
        const result = await dispatch(login(authStore.data === null || authStore.data === undefined ? '' : authStore.data?.email, data.password));
        if (result.token && result.token !== '') {
            setDialogOpen(false);
            if (logOutIdleTimeout !== null) {
                clearTimeout(logOutIdleTimeout);
            }
            idleTimer?.reset();
        } else {

            if (result.result == 1) {
                setErrorMessage("Sign-in is blocked\nYou've tried to sign in too many times with an incorrect account or password.");
            }
            else if (result.result == 4) {
                setErrorMessage("Your account is disabled at this time. For more information, please contact us on 1300 30 40 63");
            }
            else if (result.result == 9) {
                setErrorMessage(amountActiveSessionExceeded);
            }
            else if (result.result == 8) {
                setErrorMessage("Account disabled");
            }
            else {
                setErrorMessage("Username or password is invalid.");
            }
        }

    }
    const handleLogOut = () => {

        dispatch(logOut());
        history.push('/login');

    }
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => {

        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    setIdleTimes(authStore?.data);

    return <React.Fragment>
        <IdleTimer
            ref={ref => { idleTimer = ref }}
            timeout={1000 * 60 *idleTimeInMinutes}

            onIdle={handleOnIdle}

            debounce={250}
        />
        <Dialog open={isDialogOpen} aria-labelledby="form-dialog-title" fullWidth={true} maxWidth="sm">
            <DialogTitle id="form-dialog-title">Are you still there</DialogTitle>
            <form onSubmit={handleSubmit(handleOnLogin)}>
                <DialogContent>
                    <DialogContentText>
                        Enter password to continue working
                    </DialogContentText>
                    <Controller
                        defaultValue=''
                        control={control}
                        as={<TextField InputProps={{

                            endAdornment: <InputAdornment position="end">
                                <IconButton style={{ color: '#000' }}
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}>
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        }} variant='outlined' placeholder='Password' type={showPassword ? 'text' : 'password'} error={errors.password} helperText={errors.password ? errors.password.message : ""} />}
                        name='password'
                        rules={{
                            required: "Password is required"
                        }}
                    />
                    <Alert variant="filled" icon={<ReportProblemOutlinedIcon />} severity="error" classes={{ root: classes.alertErrorRoot }} style={{ display: errorMessage ? 'flex' : 'none' }}>
                        {errorMessage}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleOnReturnToLogin} color="secondary">
                        Return to Login
                    </Button>
                    <Button type="submit" color="primary">
                        Continue Working
          </Button>
                </DialogActions>
            </form>
        </Dialog></React.Fragment>;
}

export default React.memo(AutoLogout) 
